import type { ReportConfig } from '../../../domain/cr-report-type.enum'
import type { ExpandedRowProps } from '../types'
import type { Attachment } from '../../../store/previewer/types'

import { CaretRightOutlined, DatabaseOutlined, DownloadOutlined } from '@ant-design/icons'
import { v4 as uuid } from '@lukeed/uuid'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tree
} from 'antd'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { CRReportType } from '../../../domain/cr-report-type.enum'
import { PushType } from '../../../domain/push-type.enum'
import $api, { API_URL } from '../../../API'
import { useAppDispatch } from '../../../store'
import * as previewActions from '../../../store/previewer'

type HistoryNode = {
  key: string
  title: ReactNode
  isLeaf?: boolean
  children?: HistoryNode[]
  nodeType: 'year' | 'month' | 'file'
  year?: string
  month?: string
}

type HistoricalFile = {
  name: string
  size: number
  modifyTime: number
}

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit'
})

function formatBytes(bytes: number): string {
  const measures = 'KMGT'

  for (let i = measures.length; i > 0; --i) {
    const coef = 2 ** (i * 10)

    if (bytes >= coef) {
      return `${(bytes / coef).toFixed(1)} ${measures[i - 1]}iB`
    }
  }

  return `${bytes} bytes`
}

function getMimeType(fileName: string): string | undefined {
  const lower = fileName.toLowerCase()
  if (lower.endsWith('.pdf')) {
    return 'application/pdf'
  }
  if (lower.endsWith('.csv')) {
    return 'text/csv'
  }
  if (lower.endsWith('.tsv') || lower.endsWith('.psv')) {
    return 'text/csv'
  }
  if (lower.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  }
  if (lower.endsWith('.xml')) {
    return 'text/xml'
  }
  if (lower.endsWith('.txt')) {
    return 'text/plain'
  }
  return undefined
}

export function ExpandedRow({
  row,
  form,
  isSubmitting,
  onSave,
  onCancel
}: ExpandedRowProps): JSX.Element {
  const network = Form.useWatch('network', form)
  const dispatch = useAppDispatch()
  const [historyNodes, setHistoryNodes] = useState<HistoryNode[]>([])

  const yearsQuery = useQuery({
    queryKey: ['new-groups-historical-years', row?.number],
    enabled: typeof row?.number === 'string',
    queryFn: async () => {
      const params = new URLSearchParams({ groupNumber: row?.number ?? '' })
      const { data } = await $api.get<{ years: string[] }>(
        `/groups/historical/years?${params.toString()}`
      )
      return data.years
    }
  })

  useEffect(() => {
    if (!yearsQuery.data) {
      return
    }

    setHistoryNodes(
      yearsQuery.data.map(year => ({
        key: `year-${year}`,
        title: year,
        nodeType: 'year',
        year
      }))
    )
  }, [yearsQuery.data])

  const updateHistoryNodes = useCallback(
    (list: HistoryNode[], key: string, children: HistoryNode[]): HistoryNode[] =>
      list.map(node => {
        if (node.key === key) {
          return { ...node, children }
        }
        if (node.children) {
          return { ...node, children: updateHistoryNodes(node.children, key, children) }
        }
        return node
      }),
    []
  )

  const loadHistoryData = useCallback(
    async (node: HistoryNode) => {
      if (!row?.number) {
        return
      }

      if (node.nodeType === 'year' && node.year) {
        const params = new URLSearchParams({ groupNumber: row.number, year: node.year })
        const { data } = await $api.get<{ months: string[] }>(
          `/groups/historical/months?${params.toString()}`
        )
        const children = data.months.map(month => ({
          key: `year-${node.year}-month-${month}`,
          title: month,
          nodeType: 'month' as const,
          year: node.year,
          month
        }))

        setHistoryNodes(prev => updateHistoryNodes(prev, node.key, children))
        return
      }

      if (node.nodeType === 'month' && node.year && node.month) {
        const params = new URLSearchParams({
          groupNumber: row.number,
          year: node.year,
          month: node.month
        })
        const { data } = await $api.get<{ files: HistoricalFile[] }>(
          `/groups/historical/files?${params.toString()}`
        )
        const children = data.files.map(file => {
          const params = new URLSearchParams({
            groupNumber: row.number,
            ...(node.year ? { year: node.year } : {}),
            ...(node.month ? { month: node.month } : {}),
            file: file.name
          })
          const downloadUrl = `${API_URL}/groups/historical/files/download?${params.toString()}`
          const attachment: Attachment = {
            href: downloadUrl,
            mimeType: getMimeType(file.name),
            name: file.name
          }

          return {
            key: `year-${node.year}-month-${node.month}-file-${file.name}`,
            title: (
              <span className='flex items-center gap-2'>
                <button
                  type='button'
                  className='text-left text-blue-600 hover:underline'
                  onClick={event => {
                    event.stopPropagation()
                    dispatch(previewActions.open(attachment))
                  }}
                >
                  {file.name} • {formatBytes(file.size)} •{' '}
                  {dateTimeFormatter.format(new Date(file.modifyTime * 1000))}
                </button>
                <a href={downloadUrl} className='text-blue-600' onClick={e => e.stopPropagation()}>
                  <DownloadOutlined />
                </a>
              </span>
            ),
            nodeType: 'file' as const,
            isLeaf: true
          }
        })
        setHistoryNodes(prev => updateHistoryNodes(prev, node.key, children))
      }
    },
    [dispatch, row?.number, updateHistoryNodes]
  )

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        className='p-6'
        colon={false}
        requiredMark={false}
        labelCol={{}}
        wrapperCol={{
          className: '-mt-1.5'
        }}
      >
        <Collapse
          defaultActiveKey={['1']}
          ghost
          className='flex flex-col'
          expandIcon={({ isActive }) => (
            <CaretRightOutlined
              className={isActive ? 'opacity-75' : 'opacity-50'}
              rotate={isActive ? 90 : 0}
            />
          )}
        >
          <div style={{ maxWidth: '62.5%' }}>
            <Row>
              <Col span={8}>
                <div />
              </Col>
              <Col span={16}>
                <Alert
                  className='mb-2 w-full'
                  showIcon
                  message='The fields editing functionality is disabled till going live with portal as master data source for LHC for this data.'
                  type='warning'
                />
              </Col>
            </Row>
          </div>

          <Collapse.Panel key='4' forceRender header={<h3 className='font-semibold'>General</h3>}>
            <div className='grid grid-cols-3 gap-y-4 gap-x-12 px-24'>
              <Form.Item name='checks_only' valuePropName='checked' className='mb-0'>
                <div className='flex gap-2 items-center'>
                  <Checkbox disabled />
                  <span className='flex gap-2 items-center font-semibold'>
                    <span>Checks Only</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                </div>
              </Form.Item>

              <Form.Item name='zelis_eligible' valuePropName='checked' className='mb-0'>
                <div className='flex gap-2 items-center'>
                  <Checkbox disabled />
                  <span className='flex gap-2 items-center font-semibold'>
                    <span>Zelis Eligible</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                </div>
              </Form.Item>

              <Form.Item name='full_amount' valuePropName='checked' className='mb-0'>
                <div className='flex gap-2 items-center'>
                  <Checkbox disabled />
                  <span className='flex gap-2 items-center font-semibold'>
                    <span>Full Amount</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                </div>
              </Form.Item>

              <Form.Item name='level_funded' valuePropName='checked' className='mb-0'>
                <div className='flex gap-2 items-center'>
                  <Checkbox disabled />
                  <span className='flex gap-2 items-center font-semibold'>
                    <span>Level Funded</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                </div>
              </Form.Item>

              <Form.Item name='activation_date' className='mb-0'>
                <div className='flex gap-2 items-center'>
                  <span className='flex gap-2 items-center font-semibold whitespace-nowrap'>
                    <span>Activation Date</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                  <DatePicker
                    disabled
                    size='large'
                    className='rounded-md'
                    placeholder='Select Date'
                    format='MM/DD/YYYY'
                  />
                </div>
              </Form.Item>

              <Form.Item name='status' valuePropName='checked' className='mb-0'>
                <div className='flex gap-2 items-center'>
                  <Checkbox disabled />
                  <span className='flex gap-2 items-center font-semibold'>
                    <span>Status</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                </div>
              </Form.Item>
            </div>
          </Collapse.Panel>

          <div style={{ maxWidth: '62.5%' }}>
            <Form.Item
              className='mt-4'
              name='push_type'
              label={
                <span className='flex gap-2 items-center'>
                  <span>Push Type</span>
                  <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                </span>
              }
            >
              <Select
                disabled
                options={[
                  { value: PushType.Push, label: 'Push' },
                  { value: PushType.Pull, label: 'Pull' },
                  { value: PushType.FBB, label: 'FBB' }
                ]}
                placeholder='Select push type'
                className='w-48'
                size='large'
                defaultValue={PushType.Push}
              />
            </Form.Item>
          </div>

          <Collapse.Panel
            key='5'
            forceRender
            header={<h3 className='font-semibold'>Group Account Information</h3>}
          >
            <Col span={15}>
              <Form.Item
                name='group_account_number'
                label={
                  <span className='flex gap-2 items-center'>
                    <span>Group Account Number</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                }
              >
                <Input placeholder='Group Account Number' disabled />
              </Form.Item>

              <Form.Item
                name='group_routing_number'
                label={
                  <span className='flex gap-2 items-center'>
                    <span>Group Routing Number</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                }
              >
                <Input placeholder='Group Routing Number' disabled />
              </Form.Item>

              <Form.Item
                name='group_banking_id'
                label={
                  <span className='flex gap-2 items-center'>
                    <span>ID Number</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                }
              >
                <Input placeholder='ID Number' disabled />
              </Form.Item>
              <Form.Item
                name='group_originator_id'
                label={
                  <span className='flex gap-2 items-center'>
                    <span>Originator ID</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                }
              >
                <Input placeholder='Originator ID' disabled />
              </Form.Item>
            </Col>
          </Collapse.Panel>

          {row && (
            <Collapse.Panel
              key='history'
              forceRender
              header={<h3 className='font-semibold'>Historical Checkruns</h3>}
            >
              <Tree
                treeData={historyNodes}
                loadData={node => loadHistoryData(node as HistoryNode)}
                showLine
                selectable={false}
              />
            </Collapse.Panel>
          )}

          <Collapse.Panel
            key='6'
            forceRender
            header={<h3 className='font-semibold'>Claims Account Information</h3>}
          >
            <Col span={15}>
              <Form.Item
                name='claims_account_number'
                label={
                  <span className='flex gap-2 items-center'>
                    <span>Claims Account Number</span>
                    <DatabaseOutlined className='text-blue-500' title='Synced with LHC database' />
                  </span>
                }
              >
                <Input disabled placeholder='Claims Account Number' />
              </Form.Item>

              <Form.Item name='claims_routing_number' label='Claims Routing Number'>
                <Input disabled placeholder='Claims Routing Number' />
              </Form.Item>
              <Form.Item name='claims_banking_id' label='Banking ID'>
                <Input disabled placeholder='Banking ID' />
              </Form.Item>
              <Form.Item name='claims_originator_id' label='Originator ID'>
                <Input disabled placeholder='Originator ID' />
              </Form.Item>
            </Col>
          </Collapse.Panel>

          <div style={{ maxWidth: '62.5%' }}>
            <Form.Item name='network' label='Network'>
              <Select
                options={[
                  {
                    value: 'none',
                    label: 'None'
                  },
                  {
                    value: 'nomi',
                    label: 'Nomi'
                  },
                  {
                    value: 'quantify',
                    label: 'Quantify'
                  }
                ]}
                onChange={value => {
                  const item: string = value

                  const prevReports = (form.getFieldValue('reports') as ReportConfig[]) ?? []

                  if (item === 'none') {
                    form.setFieldValue(
                      'reports',
                      prevReports.filter(
                        report =>
                          report.type !== CRReportType.NetworkDetail &&
                          report.type !== CRReportType.NetworkSummary
                      )
                    )

                    return
                  }

                  const hasCurrentReportsNetworkDetailType = prevReports.some(
                    report => report.type === CRReportType.NetworkDetail
                  )

                  const hasCurrentReportsNetworkSummaryType = prevReports.some(
                    report => report.type === CRReportType.NetworkSummary
                  )

                  const currentReports = [
                    !hasCurrentReportsNetworkDetailType
                      ? {
                          id: uuid(),
                          type: CRReportType.NetworkDetail
                        }
                      : undefined,
                    !hasCurrentReportsNetworkSummaryType
                      ? {
                          id: uuid(),
                          type: CRReportType.NetworkSummary
                        }
                      : undefined
                  ].filter(Boolean)

                  if (
                    item !== 'none' &&
                    (!hasCurrentReportsNetworkDetailType || !hasCurrentReportsNetworkSummaryType)
                  ) {
                    form.setFieldValue('reports', [...prevReports, ...currentReports])
                  }
                }}
                defaultValue={'none'}
                placeholder='Select funding type'
                size='large'
              />
            </Form.Item>
          </div>

          <Collapse.Panel
            key='7'
            forceRender
            header={<h3 className='font-semibold'>Network Account Information</h3>}
          >
            <Col span={15}>
              <Form.Item name='network_account_number' label='Network Account Number'>
                <Input placeholder='Network Account Number' disabled={network === 'none'} />
              </Form.Item>

              <Form.Item name='network_routing_number' label='Network Routing Number'>
                <Input placeholder='Network Routing Number' disabled={network === 'none'} />
              </Form.Item>
              <Form.Item name='network_banking_id' label='Banking ID'>
                <Input placeholder='Banking ID' disabled={network === 'none'} />
              </Form.Item>
              <Form.Item name='network_originator_id' label='Originator ID'>
                <Input placeholder='Originator ID' disabled={network === 'none'} />
              </Form.Item>
            </Col>
          </Collapse.Panel>
        </Collapse>
        <Row className='pt-6'>
          <Col span={24} className='flex justify-end'>
            <Space>
              <Button
                size='middle'
                type='link'
                htmlType='reset'
                className='rounded-md'
                onClick={event => {
                  event.preventDefault()
                  onCancel()
                }}
              >
                Cancel
              </Button>

              <Button
                size='middle'
                type='primary'
                htmlType='submit'
                loading={isSubmitting}
                onClick={event => {
                  event.preventDefault()
                  onSave()
                }}
              >
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </>
  )
}
